import { Component, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CuiButtonComponent, CuiInputComponent, CuiPasswordComponent } from '@idealink-material/ui-core';
import { CuiIconComponent } from '@idealink-material/ui-icons';
import { AUTH_SERVICE_TOKEN } from '@/core/util/auth.token';
import { IAuthCredentials, IAuthSession } from '@/core/interface/auth-service.interface';
import { AES } from '@/core/service/aes';
import { MsgService } from '@/core/service/msg.service';
import { SessionMonitorService } from '@/core/service/session-monitor.service';
import { STORAGE_KEYS } from '@/share/constant/storage_token.key';
import { STORAGE_SERVICE_TOKEN } from '@/core/util/storage.token';
import { SessionStatus } from '@/core/model/session-status';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-session-duration-locked',
  template: `
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(17, 24, 39, 0.5)"
    >
      <div
        class="rounded-xl w-full max-w-sm p-6 relative"
        style="background: var(--bg-card); box-shadow: var(--shadow-card)"
      >
        <button type="button" class="absolute top-4 right-4 cursor-pointer" (click)="closed()">
          <cui-icon name="close" size="sm" color="var(--text-muted)" />
        </button>

        <h3 class="text-lg font-bold text-center mb-1" style="color: var(--text-primary)">
          {{ 'session.timeout.title' | translate }}
        </h3>
        <p class="text-sm text-center mb-6" style="color: var(--text-muted)">
          {{ 'session.timeout.message' | translate }}
          re-authenticate.
        </p>

        <form [formGroup]="form" (ngSubmit)="login()">
          <div class="flex flex-col gap-4 w-full">
            <div class="flex flex-col gap-2">
              <label for="address" style="color: var(--text-muted)" class="text-xs font-medium"
                >Email</label
              >
              <p-input
                id="address"
                type="text"
                formControlName="username"
                placeholder="Email address"
                [fullWidth]="true"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label for="password" style="color: var(--text-muted)" class="text-xs font-medium"
                >Password</label
              >
              <p-password
                id="password"
                formControlName="password"
                placeholder="Password"
                [toggleMask]="true"
                [fullWidth]="true"
                [feedback]="false"
              />
            </div>

            <p-button [loading]="loading()" [fullWidth]="true" type="submit">Login</p-button>
          </div>
        </form>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CuiButtonComponent,
    CuiInputComponent,
    CuiPasswordComponent,
    CuiIconComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class SessionDurationLocked {
  onHide = output<void>();
  form!: FormGroup;
  loading = signal(false);
  private storageService = inject(STORAGE_SERVICE_TOKEN);
  private auth: IAuthSession & IAuthCredentials = inject(AUTH_SERVICE_TOKEN);

  constructor(
    private aes: AES,
    private msgService: MsgService,
    public sessionMonitor: SessionMonitorService,
    private formBuilder: FormBuilder,
  ) {
    this.initForm();
  }

  private initForm() {
    this.form = this.formBuilder.group({
      username: [{ value: 'super-admin', disabled: true }, Validators.required],
      password: [null, Validators.required],
      rememberMe: false,
    });
  }

  login(): void {
    this.loading.set(true);
    // const encryptedPassword = this.aes.encryptGcm(this.password());

    const payload = {
      username: 'super-admin',
      password: 'super@123',
      rememberMe: true,
    };

    this.auth.login(payload).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res.response.code === 200) {
          this.storageService.set(STORAGE_KEYS.TOKEN, res.results.token);
          this.storageService.set(STORAGE_KEYS.TOKEN, res.results.refreshToken);
          this.sessionMonitor.initTimer();
          this.closeDialog();
        } else {
          this.msgService.error('Login Failed', res.response.message);
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        this.msgService.error('Exception', err.message);
      },
    });
  }

  closed(): void {
    this.closeDialog();
    this.sessionMonitor.setStatus(SessionStatus.Active);
    this.auth.callLogout().subscribe({
      next: () => this.auth.logout(),
      error: () => this.auth.logout(),
    });
  }

  onModalHide(): void {
    this.closeDialog();
  }

  private closeDialog(): void {
    this.sessionMonitor.destroy();
    this.resetForm();
    this.onHide.emit();
  }

  private resetForm(): void {
    this.loading.set(false);
  }
}
